import React, { useMemo, useState } from "react";
import Tooltip from "common/Tooltips/Tooltip";
import styled, { css, useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import IconWithFallback from "Global/IconWithFallback";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { ICON_TYPES } from "@shared/common/constants";
import {
  DISPLAY_CURRENCIES,
  PORTFOLIO_ACCOUNTS_PATH,
  TIME_RANGE_1_DAY,
} from "common/constants";
import { ConsolidatedAccountData, LanguageNameSpaces } from "@shared/types";
import {
  getFirstLastInGraphData,
  getPriceChangePercentage,
} from "features/dashboard/common/utils";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { getAccountExtraDetailsRoute } from "utils/routes";
import { sharedLib } from "@shared/services/shared.service";
import AccountsEmpty from "features/portfolio/Accounts/AccountsEmpty";
import { useCreateAccountClick } from "features/portfolio/common/hooks.tsx";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { monotonePath } from "common/NewLook/monotonePath.ts";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";

interface AccountsPanelProps {
  /** Each account once (see useDashboardAccounts), so the shares add up. */
  accountsData: ConsolidatedAccountData[];
  maxRows?: number;
}

interface AccountRow {
  key: string | number;
  clientId: number;
  name: string;
  exchange: string;
  total?: number;
  values: number[];
  change?: number;
}

/**
 * Where the money is: one allocation bar across all accounts, then a row per
 * account with its value, its 24h move and a small trend line. Each row's
 * colour mark matches its slice of the bar.
 */
const AccountsPanel = ({ accountsData, maxRows = 6 }: AccountsPanelProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const isUsd = displayCurrency === DISPLAY_CURRENCIES.USD;
  const valueIndex = isUsd ? 1 : 2;
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();
  const { historyPush } = useHistoryWithQuery();
  const location = useLocation();
  const { isLoading: portfolioLoading } =
    sharedLib.sharedApi.portfolio.endpoints.getPortfolioData.useQueryState(
      undefined
    );
  const { data: clients, isLoading: clientsLoading } =
    sharedLib.sharedApi.configuration.useGetClientsListQuery();
  const { onCreateAccountClick, whopModal } = useCreateAccountClick();
  // The chart palette plus two calm extras, so six accounts never repeat a
  // colour in the allocation bar.
  const palette = [...theme.colors.pieChartColors, "#14B8A6", "#64748B"];

  const rows: AccountRow[] = useMemo(() => {
    if (accountsData.length)
      return accountsData
        .slice()
        .sort((a, b) => b.total_in_usd - a.total_in_usd)
        .map((account) => {
          const graphData = account.graph_data?.[TIME_RANGE_1_DAY] || [];
          const [first, last] = getFirstLastInGraphData(graphData, valueIndex);
          return {
            key: account.client_uuid,
            clientId: account.client_id,
            name: account.client,
            exchange: account.exchange,
            total: isUsd ? account.total_in_usd : account.total_in_btc,
            values: graphData
              .map((point) => Number(point[valueIndex]))
              .filter((value) => Number.isFinite(value)),
            change: graphData.length
              ? getPriceChangePercentage(first, last)
              : undefined,
          };
        });
    return (clients || [])
      .filter((client) => client.is_valid)
      .map((client) => ({
        key: client.client_id,
        clientId: client.client_id,
        name: client.client_name,
        exchange: client.exchange,
        values: [],
      }));
  }, [accountsData, clients, isUsd, valueIndex]);

  const total = rows.reduce((sum, row) => sum + (row.total || 0), 0);
  const visible = rows.slice(0, maxRows);
  const rest = rows.slice(maxRows);
  const restTotal = rest.reduce((sum, row) => sum + (row.total || 0), 0);
  const loading = !rows.length && (portfolioLoading || clientsLoading);
  const colorOf = (index: number) => palette[index % palette.length];
  // One highlighted account across the bar and the list: hovering a slice
  // lights its row and hovering a row lights its slice.
  const [hoveredKey, setHoveredKey] = useState<string | number>();
  const openAccount = (clientId: number) =>
    historyPush(getAccountExtraDetailsRoute(location.pathname, clientId));
  const shareOf = (value: number) => {
    const share = total ? (value / total) * 100 : 0;
    return `${share < 1 ? "<1" : Math.round(share)}%`;
  };

  const money = (value: number) =>
    `${isUsd ? "$" : "₿"}${getLocaleStringWithPrecision(value, precision)}`;

  const renderBody = () => {
    if (loading)
      return (
        <List>
          {[0, 1, 2].map((index) => (
            <Row key={index} as="div" $static={true}>
              <Skeleton width={32} height={32} radius={8} />
              <Names>
                <Skeleton width={110} height={12} />
                <Skeleton width={70} height={10} />
              </Names>
              <span />
              <End>
                <Skeleton width={72} height={12} />
              </End>
            </Row>
          ))}
        </List>
      );
    if (!rows.length)
      return (
        <EmptyWrapper>
          <AccountsEmpty buttonOnClick={onCreateAccountClick} iconWidth={88} />
        </EmptyWrapper>
      );
    return (
      <>
        {total > 0 && (
          <Allocation>
            <AllocationBar
              role="img"
              aria-label={visible
                .map(
                  (row) =>
                    `${row.name} ${Math.round(
                      ((row.total || 0) / total) * 100
                    )}%`
                )
                .join(", ")}
            >
              {visible.map((row, index) => (
                <Tooltip
                  key={row.key}
                  title={`${row.name} · ${money(row.total || 0)} · ${shareOf(
                    row.total || 0
                  )}`}
                  mouseEnterDelay={0}
                >
                  <Segment
                    $dimmed={hoveredKey !== undefined && hoveredKey !== row.key}
                    style={{
                      flexGrow: row.total || 0,
                      background: colorOf(index),
                    }}
                    onMouseEnter={() => setHoveredKey(row.key)}
                    onMouseLeave={() => setHoveredKey(undefined)}
                    onClick={() => openAccount(row.clientId)}
                  />
                </Tooltip>
              ))}
              {restTotal > 0 && (
                <Tooltip
                  title={`${t("overview.other_accounts", {
                    count: rest.length,
                  })} · ${money(restTotal)} · ${shareOf(restTotal)}`}
                  mouseEnterDelay={0}
                >
                  <Segment
                    $dimmed={hoveredKey !== undefined && hoveredKey !== "rest"}
                    style={{ flexGrow: restTotal, background: tokens.muted }}
                    onMouseEnter={() => setHoveredKey("rest")}
                    onMouseLeave={() => setHoveredKey(undefined)}
                  />
                </Tooltip>
              )}
            </AllocationBar>
          </Allocation>
        )}
        <List>
          {visible.map((row, index) => {
            const share = total ? ((row.total || 0) / total) * 100 : undefined;
            return (
              <Row
                key={row.key}
                role="button"
                tabIndex={0}
                $active={hoveredKey === row.key}
                onMouseEnter={() => setHoveredKey(row.key)}
                onMouseLeave={() => setHoveredKey(undefined)}
                onFocus={() => setHoveredKey(row.key)}
                onBlur={() => setHoveredKey(undefined)}
                onClick={() => openAccount(row.clientId)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") openAccount(row.clientId);
                }}
              >
                <Logo>
                  <IconWithFallback
                    coinOrExchangeName={row.exchange}
                    iconType={ICON_TYPES.EXCHANGE}
                    pxIconSize={18}
                  />
                </Logo>
                <Names>
                  <Name title={row.name}>{row.name}</Name>
                  <Sub>
                    <LegendDot style={{ background: colorOf(index) }} />
                    {row.exchange}
                    {share !== undefined && (
                      <>
                        <Dot>·</Dot>
                        {share < 1 ? "<1" : Math.round(share)}%
                      </>
                    )}
                  </Sub>
                </Names>
                <Trend
                  values={row.values}
                  color={
                    row.change
                      ? directionColor(tokens, row.change)
                      : tokens.muted
                  }
                />
                <End>
                  {row.total !== undefined ? (
                    <Value>{money(row.total)}</Value>
                  ) : (
                    <Skeleton width={64} height={12} />
                  )}
                  {row.change !== undefined && (
                    <Change
                      style={{ color: directionColor(tokens, row.change) }}
                    >
                      {row.change > 0 ? "+" : row.change < 0 ? "−" : ""}
                      {Math.abs(row.change).toFixed(2)}%
                    </Change>
                  )}
                </End>
              </Row>
            );
          })}
        </List>
      </>
    );
  };

  return (
    <Panel
      title={t("accounts.title")}
      count={rows.length || undefined}
      seeAllTo={PORTFOLIO_ACCOUNTS_PATH}
      seeAllLabel={tCommon("labels.see_all")}
      flush={true}
      data-testid="dashboard-accounts"
    >
      {renderBody()}
      {whopModal}
    </Panel>
  );
};

export default React.memo(AccountsPanel);

const TREND_WIDTH = 56;
const TREND_HEIGHT = 22;

const Trend = ({ values, color }: { values: number[]; color: string }) => {
  const shape = useMemo(() => {
    if (values.length < 3) return undefined;
    const min = Math.min(...values);
    const max = Math.max(...values);
    // A balance that didn't move (e.g. all stablecoins) still gets its line,
    // level through the middle, so "no change" doesn't read as "no data".
    if (max === min) return `M0 ${TREND_HEIGHT / 2} H${TREND_WIDTH}`;
    const points = values.map(
      (value, index) =>
        [
          (index / (values.length - 1)) * TREND_WIDTH,
          2 + (1 - (value - min) / (max - min)) * (TREND_HEIGHT - 4),
        ] as [number, number]
    );
    return monotonePath(points);
  }, [values]);
  if (!shape) return <span />;
  return (
    <svg
      width={TREND_WIDTH}
      height={TREND_HEIGHT}
      viewBox={`0 0 ${TREND_WIDTH} ${TREND_HEIGHT}`}
      aria-hidden={true}
      style={{ overflow: "visible", justifySelf: "end" }}
    >
      <path
        d={shape}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const Allocation = styled.div`
  padding: 16px 20px 6px;
`;
const AllocationBar = styled.div`
  display: flex;
  gap: 2px;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
`;
const Segment = styled.span<{ $dimmed?: boolean }>`
  flex-basis: 0;
  min-width: 3px;
  cursor: pointer;
  opacity: ${(p) => (p.$dimmed ? 0.3 : 1)};
  transition: opacity 150ms;
`;
const List = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 0 8px;
`;
const Row = styled.div<{ $static?: boolean; $active?: boolean }>`
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) ${TREND_WIDTH}px minmax(84px, auto);
  align-items: center;
  column-gap: 12px;
  min-height: 56px;
  padding: 8px 20px;
  outline: none;
  transition: background-color 120ms;
  ${(p) =>
    !p.$static &&
    css`
      cursor: pointer;
      &:hover {
        background: ${getNewLookTokens(p.theme).rowHover};
      }
      &:focus-visible {
        box-shadow: inset 0 0 0 2px ${getNewLookTokens(p.theme).focus};
      }
    `}
  ${(p) =>
    p.$active &&
    css`
      background: ${getNewLookTokens(p.theme).rowHover};
    `}
`;
const Logo = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
// Ties the row to its slice of the allocation bar.
const LegendDot = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-right: 6px;
  border-radius: 2px;
  vertical-align: 0;
`;
const Names = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;
const Name = styled.span`
  font-size: 14px;
  line-height: 18px;
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Sub = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Dot = styled.span`
  margin: 0 5px;
`;
const End = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;
const Value = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const Change = styled.span`
  ${newLookType.meta};
  font-weight: 500;
`;
const EmptyWrapper = styled.div`
  padding: 8px 20px 20px;
`;
